import { useRef } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Camera, Zap, QrCode, LayoutGrid, Lock, Users, Download, Heart, Check } from 'lucide-react'
import { ThumbWall } from './ThumbWall'
import { useParallax } from './hooks'
import { img, POOL } from './media'
import { Section, Eyebrow, GBtn } from './primitives'

export function Hero() {
  const wallRef = useRef<HTMLDivElement>(null)
  useParallax(wallRef, 0.14)
  const trust = ['Free to start', 'Guests never sign up', 'Originals, not compressed', 'Download the whole album']
  return (
    <header id="top" style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
      <div ref={wallRef} style={{ position: 'absolute', inset: 0 }}>
        <ThumbWall columns={6} perCol={6} />
      </div>
      <div className="pd-scrim hero" />
      <div style={{ position: 'relative', maxWidth: 1000, margin: '0 auto', padding: '120px 28px 90px', textAlign: 'center' }}>
        <div className="pd-reveal in" style={{ display: 'flex', justifyContent: 'center', marginBottom: 26 }}>
          <span className="pd-pill">
            <span style={{ width: 8, height: 8, borderRadius: 99, background: 'hsl(var(--secondary))' }} />
            No app. No accounts. Just photos.
          </span>
        </div>
        <h1 className="fdisplay pd-hero-headline" style={{ fontSize: 'clamp(2.7rem, 6.4vw, 5.4rem)', lineHeight: 0.98, letterSpacing: '-.035em', fontWeight: 800, margin: 0 }}>
          Collect everyone's photos.<br />
          <span className="pd-grad-text">No accounts. No hassle.</span>
        </h1>
        <p className="pd-hero-headline" style={{ maxWidth: 640, margin: '26px auto 0', fontSize: 'clamp(1.05rem, 1.7vw, 1.35rem)', lineHeight: 1.5, color: 'hsl(var(--foreground)/.8)' }}>
          Stop chasing folks over email, texts and clunky upload links. Create a drop, share one link, and everyone adds their photos in seconds — no sign-ups, no downloads, full quality.
        </p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginTop: 36 }}>
          <GBtn><Button asChild variant="playful" size="xl"><Link to="/signup">Create a drop — it's free</Link></Button></GBtn>
          <Button asChild variant="outline" size="xl"><a href="#how">See how it works</a></Button>
        </div>
        <div style={{ display: 'flex', gap: '10px 26px', justifyContent: 'center', flexWrap: 'wrap', marginTop: 30, color: 'hsl(var(--foreground)/.66)', fontWeight: 600, fontSize: '.92rem' }}>
          {trust.map((t) => (
            <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <Check style={{ width: 15, height: 15, color: 'hsl(var(--secondary))' }} />{t}
            </span>
          ))}
        </div>
      </div>
    </header>
  )
}

export function HowItWorks() {
  const steps = [
    { Icon: Zap, t: 'Create a drop', d: 'Name your event and get a shareable link and QR code instantly. Takes about ten seconds.' },
    { Icon: QrCode, t: 'Share the link', d: 'Text it, print the QR on a table card, or post it in the group chat. Anyone can open it.' },
    { Icon: LayoutGrid, t: 'Photos roll in', d: 'Guests add photos straight from their camera roll — no account, no app. Watch the album fill up live.' },
  ]
  return (
    <Section id="how">
      <div className="pd-reveal" style={{ textAlign: 'center', maxWidth: 700, margin: '0 auto 60px' }}>
        <Eyebrow>How it works</Eyebrow>
        <h2 className="fdisplay" style={{ fontSize: 'clamp(2rem,3.6vw,3rem)', letterSpacing: '-.03em', fontWeight: 800, margin: 0 }}>Three steps. Zero friction.</h2>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 22 }}>
        {steps.map((s, i) => (
          <div key={s.t} className="pd-card pd-reveal" style={{ padding: 30, transitionDelay: `${i * 90}ms` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
              <span style={{ width: 52, height: 52, borderRadius: 15, display: 'grid', placeItems: 'center', background: 'hsl(var(--primary)/.12)', color: 'hsl(var(--primary))' }}>
                <s.Icon style={{ width: 24, height: 24 }} />
              </span>
              <span className="fdisplay" style={{ fontSize: '2.6rem', fontWeight: 800, color: 'hsl(var(--border))', lineHeight: 1 }}>0{i + 1}</span>
            </div>
            <h3 className="fdisplay" style={{ fontSize: '1.4rem', fontWeight: 700, margin: '0 0 8px' }}>{s.t}</h3>
            <p style={{ margin: 0, color: 'hsl(var(--muted-foreground))', lineHeight: 1.55 }}>{s.d}</p>
          </div>
        ))}
      </div>
    </Section>
  )
}

export function Features() {
  const feats = [
    { Icon: Lock, t: 'No sign-ups for guests', d: "Guests never create an account or install anything. One tap and they're in." },
    { Icon: Camera, t: 'Full-quality originals', d: 'We keep every photo at original resolution — no messy compression or screenshots.' },
    { Icon: Users, t: 'Everyone contributes', d: 'Dozens of people can add to the same album at once. See who added what.' },
    { Icon: Download, t: 'One-click download', d: 'Grab the entire album as a zip, or hand-pick your favourites in full res.' },
    { Icon: Heart, t: 'Live, together', d: 'New photos appear in real time — perfect on a big screen at the party.' },
    { Icon: Lock, t: 'Private by default', d: 'Only people with the link can view or add. Close the drop whenever you like.' },
  ]
  return (
    <div style={{ background: 'hsl(var(--muted)/.5)', borderTop: '1px solid hsl(var(--border))', borderBottom: '1px solid hsl(var(--border))' }}>
      <Section id="features">
        <div className="pd-reveal" style={{ maxWidth: 640, marginBottom: 54 }}>
          <Eyebrow color="secondary">Why people love it</Eyebrow>
          <h2 className="fdisplay" style={{ fontSize: 'clamp(2rem,3.6vw,3rem)', letterSpacing: '-.03em', fontWeight: 800, margin: 0 }}>Everything you need to gather the whole story.</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 18 }}>
          {feats.map((f, i) => (
            <div key={f.t} className="pd-reveal" style={{ display: 'flex', gap: 16, padding: '24px 22px', borderRadius: 'var(--radius-xl)', background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', transitionDelay: `${(i % 3) * 80}ms` }}>
              <span style={{ flex: 'none', width: 44, height: 44, borderRadius: 13, display: 'grid', placeItems: 'center', background: 'hsl(var(--accent)/.16)', color: 'hsl(var(--accent))' }}>
                <f.Icon style={{ width: 21, height: 21 }} />
              </span>
              <div>
                <h3 className="fdisplay" style={{ fontSize: '1.14rem', fontWeight: 700, margin: '2px 0 6px' }}>{f.t}</h3>
                <p style={{ margin: 0, color: 'hsl(var(--muted-foreground))', lineHeight: 1.5, fontSize: '.96rem' }}>{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  )
}

export function Preview() {
  const grid = ['frame45', 'album56', 'memory63', 'group77', 'candid88', 'flash94', 'glow61', 'spark73', 'warm82']
  return (
    <Section id="preview">
      <div className="pd-preview-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 54, alignItems: 'center' }}>
        <div className="pd-reveal">
          <Eyebrow>Your live album</Eyebrow>
          <h2 className="fdisplay" style={{ fontSize: 'clamp(2rem,3.4vw,2.9rem)', letterSpacing: '-.03em', fontWeight: 800, margin: '0 0 18px' }}>Guests just scan and drop.</h2>
          <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '1.08rem', lineHeight: 1.6, maxWidth: 460 }}>
            Put a QR code on the table or drop the link in the chat. Every photo lands in one shared gallery you can relive, download, and share — long after the moment's over.
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 26, flexWrap: 'wrap' }}>
            <GBtn><Button asChild variant="playful" size="lg"><Link to="/signup">Start your first drop</Link></Button></GBtn>
            <Button asChild variant="ghost" size="lg"><a href="#how">See how it works</a></Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 30 }}>
            <div style={{ display: 'flex' }}>
              {POOL.slice(0, 4).map((s, i) => (
                <img key={s} src={img(s, 60, 60)} alt="" style={{ width: 38, height: 38, borderRadius: 99, border: '2px solid hsl(var(--background))', objectFit: 'cover', marginLeft: i ? -12 : 0 }} />
              ))}
            </div>
            <span style={{ color: 'hsl(var(--muted-foreground))', fontWeight: 600, fontSize: '.95rem' }}>One shared album everyone can add to.</span>
          </div>
        </div>
        <div className="pd-reveal" style={{ display: 'flex', justifyContent: 'center', transitionDelay: '120ms' }}>
          <div className="pd-phone" style={{ width: 300, padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 6px 14px' }}>
              <span style={{ width: 26, height: 26, borderRadius: 8, background: 'linear-gradient(135deg, hsl(var(--gradient-start)), hsl(var(--gradient-end)))', display: 'grid', placeItems: 'center', color: '#fff' }}>
                <Camera style={{ width: 14, height: 14 }} />
              </span>
              <div style={{ lineHeight: 1.1 }}>
                <div className="fdisplay" style={{ fontWeight: 700, fontSize: '.95rem' }}>Maya &amp; Theo's Wedding</div>
                <div style={{ fontSize: '.72rem', color: 'hsl(var(--muted-foreground))' }}>218 photos · 34 people</div>
              </div>
              <span className="pd-pill" style={{ marginLeft: 'auto', padding: '3px 9px', fontSize: '.64rem', color: 'hsl(var(--secondary))' }}>● Live</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
              {grid.map((s) => <img key={s} className="pd-mini" src={img(s, 200, 200)} alt="" style={{ aspectRatio: '1', height: 'auto' }} />)}
            </div>
            <button style={{ width: '100%', marginTop: 12, border: 0, borderRadius: 12, padding: '11px', fontWeight: 700, color: '#fff', cursor: 'pointer', background: 'linear-gradient(105deg, hsl(var(--gradient-start)), hsl(var(--gradient-middle)), hsl(var(--gradient-end)))', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Camera style={{ width: 16, height: 16 }} /> Add your photos
            </button>
          </div>
        </div>
      </div>
    </Section>
  )
}

export function UseCases() {
  const cases = [
    { t: 'Weddings', d: "Every guest's angle of the big day.", seed: 'wed14' },
    { t: 'Trips & reunions', d: "One album, everyone's camera roll.", seed: 'trip07' },
    { t: 'Parties', d: "The candids you'd never see otherwise.", seed: 'party33' },
    { t: 'Team & events', d: 'Crowdsource the whole conference.', seed: 'event91' },
  ]
  return (
    <Section id="usecases">
      <div className="pd-reveal" style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 50px' }}>
        <Eyebrow color="accent">Made for the moments</Eyebrow>
        <h2 className="fdisplay" style={{ fontSize: 'clamp(2rem,3.6vw,3rem)', letterSpacing: '-.03em', fontWeight: 800, margin: 0 }}>One link fits every occasion.</h2>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 16 }}>
        {cases.map((c, i) => (
          <div key={c.t} className="pd-usecase pd-reveal" style={{ minHeight: 300, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: 22, transitionDelay: `${i * 70}ms` }}>
            <img src={img(c.seed, 500, 640)} alt={c.t} />
            <h3 className="fdisplay" style={{ color: '#fff', fontSize: '1.5rem', fontWeight: 800, margin: '0 0 4px' }}>{c.t}</h3>
            <p style={{ color: 'rgba(255,255,255,.82)', margin: 0, fontSize: '.95rem' }}>{c.d}</p>
          </div>
        ))}
      </div>
    </Section>
  )
}

// Honest replacement for the design's fabricated stats + testimonials.
export function ValueProps() {
  const promises = [
    { Icon: Lock, t: 'Zero sign-ups for guests', d: 'Anyone with the link can add photos — no account, no app to install.' },
    { Icon: Camera, t: 'Originals, full quality', d: 'Every photo is stored at full resolution, never compressed or screenshotted.' },
    { Icon: Heart, t: 'Live shared album', d: 'Photos appear as they land — beautiful on a screen at the event.' },
    { Icon: Download, t: 'Yours to keep', d: 'Download the whole album, or hand-pick favourites, any time.' },
  ]
  return (
    <div style={{ background: 'hsl(var(--muted)/.5)', borderTop: '1px solid hsl(var(--border))', borderBottom: '1px solid hsl(var(--border))' }}>
      <Section id="promise">
        <div className="pd-reveal" style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 50px' }}>
          <Eyebrow color="secondary">The Photodrop promise</Eyebrow>
          <h2 className="fdisplay" style={{ fontSize: 'clamp(2rem,3.6vw,3rem)', letterSpacing: '-.03em', fontWeight: 800, margin: 0 }}>Simple for guests. Everything for you.</h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 18 }}>
          {promises.map((p, i) => (
            <div key={p.t} className="pd-card pd-reveal" style={{ padding: 26, transitionDelay: `${i * 70}ms` }}>
              <span style={{ display: 'grid', placeItems: 'center', width: 46, height: 46, borderRadius: 13, background: 'hsl(var(--primary)/.12)', color: 'hsl(var(--primary))', marginBottom: 16 }}>
                <p.Icon style={{ width: 22, height: 22 }} />
              </span>
              <h3 className="fdisplay" style={{ fontSize: '1.16rem', fontWeight: 700, margin: '0 0 6px' }}>{p.t}</h3>
              <p style={{ margin: 0, color: 'hsl(var(--muted-foreground))', lineHeight: 1.5, fontSize: '.95rem' }}>{p.d}</p>
            </div>
          ))}
        </div>
      </Section>
    </div>
  )
}

export function CTABand() {
  const wallRef = useRef<HTMLDivElement>(null)
  useParallax(wallRef, 0.08)
  return (
    <section id="pricing-cta" style={{ position: 'relative', overflow: 'hidden' }}>
      <div ref={wallRef} style={{ position: 'absolute', inset: 0 }}><ThumbWall columns={7} perCol={5} lazy /></div>
      <div className="pd-scrim band" />
      <div className="pd-reveal" style={{ position: 'relative', maxWidth: 760, margin: '0 auto', padding: 'clamp(80px,11vw,150px) 28px', textAlign: 'center' }}>
        <h2 className="fdisplay pd-hero-headline" style={{ fontSize: 'clamp(2.3rem,5vw,4rem)', letterSpacing: '-.035em', fontWeight: 800, lineHeight: 1, margin: 0 }}>
          Your next event deserves<br /><span className="pd-grad-text">every photo.</span>
        </h2>
        <p className="pd-hero-headline" style={{ maxWidth: 500, margin: '22px auto 0', fontSize: '1.15rem', color: 'hsl(var(--foreground)/.82)' }}>
          Free to create your first drop. No credit card, no app, no accounts for anyone you invite.
        </p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginTop: 34 }}>
          <GBtn><Button asChild variant="playful" size="xl"><Link to="/signup">Create a drop — free</Link></Button></GBtn>
          <GBtn><Button asChild variant="outline" size="xl"><Link to="/pricing">See pricing</Link></Button></GBtn>
        </div>
      </div>
    </section>
  )
}
